import type { PosDatabase } from '@core/db/db'
import { INITIAL_CATALOG_VERSION_NAME } from '@core/db/db'
import {
  META_ACTIVE_VERSION,
  META_ALLOW_ON_THE_FLY,
  META_STORE_CURRENCY,
  META_LOW_STOCK_THRESHOLD,
  META_FREQUENT_PRODUCTS_ENABLED,
  META_CATALOG_VERSION
} from '@core/db/schema'
import type { CurrencyCode } from '@core/utils/currency'
import { generateUUID } from '@core/utils/crypto'
import { enqueuePendingEdit } from './pendingProductEdits'
import { assignOrdinals, nextOrdinal } from './ordinal'
import {
  aggregateFrequency,
  FREQUENCY_WINDOW_MS,
  normalizeName,
  productKey,
  rollupByCategory,
  type SalesDocRef
} from './frequency'
import type { CatalogVersion, Category, Product } from '@core/db/models'
import type { ParsedCategory, ParsedRow } from '@utils/index'
import { syncService } from '@core/services/sync/SyncService'

/** Сводка состояния каталога устройства (диагностика версий). */
export interface CatalogDiagnostics {
  activeVersionId: number | null
  activeVersionName: string | null
  /** Источник активной версии: локальный импорт или облако. */
  activeVersionSource: 'local' | 'cloud' | null
  /** Строк в активной версии (включая мягко удалённые). */
  activeRows: number
  /** Живых строк в активной версии. */
  activeLive: number
  /** Уникальных пар «ШК + название» среди живых. */
  activeUnique: number
  /** Мягко удалённых в активной версии. */
  activeDeleted: number
  /** Версий в локальном реестре. */
  versions: number
  /** Всего строк в локальной БД (все версии). */
  totalRows: number
}

/**
 * Служба каталога товаров с оптимизированным версионированием.
 *
 * Ключевая идея: товары НЕ копируются при изменениях. Каждый импорт создаёт
 * новую версию (CatalogVersion), а импортируемые товары маркируются её id.
 * Активная версия хранится в store meta. Касса и админка читают только товары
 * активной версии. Откат = переключение активной версии (мгновенно, без
 * перезаписи данных).
 */
export class CatalogService {
  constructor(private readonly db: PosDatabase) {}

  /**
   * Гарантировать наличие активной версии (для свежей БД без миграции).
   * Если версий нет — создаёт стартовую и делает её активной.
   * @returns id активной версии.
   */
  async ensureActiveVersion(): Promise<number> {
    const existing = await this.getActiveVersionId()
    if (existing != null) {
      return existing
    }
    const anyVersion = await this.db.catalogVersions.orderBy('createdAt').first()
    if (anyVersion?.id != null) {
      await this.setActiveVersionId(anyVersion.id)
      return anyVersion.id
    }

    // StrictMode в dev и повторный bootstrap могут вызвать ensureActiveVersion
    // параллельно. Уникальный индекс versionName не должен ронять кассу:
    // второй вызов просто переиспользует уже созданную версию.
    try {
      const versionId = await this.db.catalogVersions.add({
        versionName: INITIAL_CATALOG_VERSION_NAME,
        note: 'Создано при первом старте',
        createdAt: Date.now()
      })
      await this.setActiveVersionId(versionId)
      return versionId
    } catch (err) {
      const existing = await this.db.catalogVersions
        .where('versionName')
        .equals(INITIAL_CATALOG_VERSION_NAME)
        .first()
      if (existing?.id != null) {
        await this.setActiveVersionId(existing.id)
        return existing.id
      }
      throw err
    }
  }

  /** id активной версии каталога или null, если ещё не задана. */
  async getActiveVersionId(): Promise<number | null> {
    const entry = await this.db.meta.get(META_ACTIVE_VERSION)
    if (typeof entry?.value === 'number') {
      return entry.value
    }
    // Резерв 1: номер облачной версии (META_CATALOG_VERSION хранится строкой).
    const cloudEntry = await this.db.meta.get(META_CATALOG_VERSION)
    if (cloudEntry?.value != null) {
      const parsed = parseInt(String(cloudEntry.value), 10)
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed
      }
    }
    // Резерв 2: максимальный id среди известных версий каталога.
    const latest = await this.db.catalogVersions.orderBy('id').last()
    return latest?.id ?? null
  }

  /** Переключить активную версию (используется и для отката). */
  async setActiveVersionId(versionId: number): Promise<void> {
    await this.db.meta.put({ key: META_ACTIVE_VERSION, value: versionId })
  }

  /** Все версии каталога, новые сверху. */
  async listVersions(): Promise<CatalogVersion[]> {
    const versions = await this.db.catalogVersions.orderBy('createdAt').toArray()
    return versions.reverse()
  }

  /** Активная версия целиком (для заголовков UI). */
  async getActiveVersion(): Promise<CatalogVersion | null> {
    const id = await this.getActiveVersionId()
    if (id == null) {
      return null
    }
    return (await this.db.catalogVersions.get(id)) ?? null
  }

  /** Товары активной версии, отсортированные по имени. */
  async listActiveProducts(): Promise<Product[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) {
      return []
    }
    return this.listProductsOfVersion(versionId)
  }

  /** Товары конкретной версии (без мягко удалённых), отсортированные по имени. */
  async listProductsOfVersion(versionId: number): Promise<Product[]> {
    const products = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    return products
      .filter((p) => !p.isDeleted)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }

  /** Количество товаров в версии (для карточек версий). */
  async countProductsOfVersion(versionId: number): Promise<number> {
    return this.db.products.where('catalogVersionId').equals(versionId).count()
  }

  /**
   * Диагностика каталога устройства: какая версия активна, откуда она и как
   * соотносятся строки/уникальные пары/удалённые. Нужна, чтобы объяснять
   * расхождения между кассами (ARCH-TASK-61.0).
   */
  async getCatalogDiagnostics(): Promise<CatalogDiagnostics> {
    const versionId = await this.getActiveVersionId()
    const [version, versions, totalRows] = await Promise.all([
      versionId != null ? this.db.catalogVersions.get(versionId) : Promise.resolve(undefined),
      this.db.catalogVersions.count(),
      this.db.products.count()
    ])

    if (versionId == null) {
      return {
        activeVersionId: null,
        activeVersionName: null,
        activeVersionSource: null,
        activeRows: 0,
        activeLive: 0,
        activeUnique: 0,
        activeDeleted: 0,
        versions,
        totalRows
      }
    }

    const rows = await this.db.products.where('catalogVersionId').equals(versionId).toArray()
    const live = rows.filter((p) => !p.isDeleted)
    const unique = new Set(live.map((p) => `${p.barcode}\u0000${p.name}`)).size

    return {
      activeVersionId: versionId,
      activeVersionName: version?.versionName ?? null,
      activeVersionSource: version?.source ?? null,
      activeRows: rows.length,
      activeLive: live.length,
      activeUnique: unique,
      activeDeleted: rows.length - live.length,
      versions,
      totalRows
    }
  }

  /**
   * Локальная дедупликация активной версии (ARCH-TASK-61.0c): одинаковые пары
   * «ШК + название» среди живых схлопываются в одну строку. Приоритет — строка
   * с cloudId (связана с облаком), затем самая новая.
   * @returns число объединённых строк.
   */
  async dedupeActiveVersion(): Promise<number> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return 0

    const rows = await this.db.products.where('catalogVersionId').equals(versionId).toArray()
    const groups = new Map<string, Product[]>()
    for (const p of rows) {
      if (p.isDeleted) continue
      const key = `${p.barcode}\u0000${p.name}`
      const list = groups.get(key)
      if (list) list.push(p)
      else groups.set(key, [p])
    }

    let merged = 0
    await this.db.transaction('rw', this.db.products, async () => {
      for (const list of groups.values()) {
        if (list.length < 2) continue
        // Больше одной облачной строки в группе — не угадываем, какую оставить:
        // удалённая облачная строка вернётся следующей дельтой.
        if (list.filter((p) => p.cloudId != null).length > 1) continue
        const sorted = [...list].sort((a, b) => {
          const aCloud = a.cloudId != null ? 1 : 0
          const bCloud = b.cloudId != null ? 1 : 0
          if (aCloud !== bCloud) return bCloud - aCloud
          return (b.id ?? 0) - (a.id ?? 0)
        })
        for (const p of sorted.slice(1)) {
          if (p.id == null) continue
          await this.db.products.update(p.id, { isDeleted: true, updatedAt: Date.now() })
          merged++
        }
      }
    })
    return merged
  }

  /**
   * Удалить локальные строки неактивных версий (ARCH-TASK-61.0c).
   * Облако — источник истины; старые локальные копии только раздувают базу
   * (7 версий ≈ 5 800 строк при каталоге ~850).
   * @returns число удалённых строк.
   */
  async pruneOldVersions(): Promise<number> {
    const activeId = await this.getActiveVersionId()
    if (activeId == null) return 0

    // Удалять копии старых версий можно только когда активна облачная версия:
    // иначе снесём локальные копии облачного каталога, оставшись на локальном.
    const active = await this.db.catalogVersions.get(activeId)
    if (active?.source !== 'cloud') {
      throw new Error(
        'Активна не облачная версия — переключитесь на «Версию N (Облако)», чтобы очистить старые копии'
      )
    }

    const all = await this.db.products.toArray()
    const doomed = all.filter((p) => p.catalogVersionId !== activeId)
    const ids = doomed.map((p) => p.id).filter((id): id is number => id != null)
    if (ids.length > 0) {
      await this.db.products.bulkDelete(ids)
    }
    return ids.length
  }

  /**
   * Первые N товаров активной версии для сетки «ходовых» на кассе.
   * MVP-эвристика: берём по имени (стабильный порядок). Когда появится учёт
   * продаж, сюда подставится реальный топ по оборачиваемости.
   */
  async topProducts(limit = 8): Promise<Product[]> {
    const products = await this.listActiveProducts()
    return products.slice(0, limit)
  }

  /**
   * Категории активной версии с количеством живых товаров в каждой.
   * Отсортированы по убыванию количества (крупные категории — первыми).
   */
  async listCategories(): Promise<{ name: string; count: number }[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return []
    const products = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    const counts: Record<string, number> = {}
    for (const p of products) {
      if (p.isDeleted) continue
      const cat = p.category?.trim() || 'Общее'
      counts[cat] = (counts[cat] || 0) + 1
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }

  /**
   * Подкатегории выбранной категории активной версии.
   * Пустые подкатегории не возвращаются; сортировка по количеству.
   */
  async listSubcategories(category: string): Promise<{ name: string; count: number }[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null || category === 'all') return []
    const products = await this.db.products
      .where('[catalogVersionId+category]')
      .equals([versionId, category])
      .toArray()
    const counts: Record<string, number> = {}
    for (const p of products) {
      if (p.isDeleted) continue
      const sub = p.subcategory?.trim()
      if (!sub) continue
      counts[sub] = (counts[sub] || 0) + 1
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }

  /**
   * Товары выбранной категории активной версии (без мягко удалённых),
   * отсортированные по имени. category === 'all' — все товары версии.
   * subcategory — необязательный фильтр внутри категории.
   */
  async listProductsByCategory(category: string, subcategory?: string): Promise<Product[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return []
    let list: Product[]
    if (category === 'all') {
      list = await this.db.products.where('catalogVersionId').equals(versionId).toArray()
    } else if (subcategory) {
      list = await this.db.products
        .where('[catalogVersionId+category+subcategory]')
        .equals([versionId, category, subcategory])
        .toArray()
    } else {
      list = await this.db.products
        .where('[catalogVersionId+category]')
        .equals([versionId, category])
        .toArray()
    }
    return list
      .filter((p) => !p.isDeleted)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }

  /**
   * Верхний уровень дерева категорий с количеством товаров в поддереве.
   * Если справочник категорий пуст, верхний уровень строится по товарам.
   */
  /**
   * Имена неактивных категорий с каскадом по детям.
   * Неактивный родитель скрывает всё поддерево.
   */
  private async inactiveCategoryNames(): Promise<Set<string>> {
    const rows = await this.db.categories.toArray()
    const inactive = new Set<string>()
    const mark = (name: string) => {
      if (inactive.has(name)) return
      inactive.add(name)
      for (const child of rows) {
        if (child.parentName === name) mark(child.name)
      }
    }
    for (const row of rows) {
      if (!row.isActive) mark(row.name)
    }
    return inactive
  }

  /** Активна ли категория товара (с учётом каскада родителей). */
  async isCategoryActive(
    categoryParent?: string,
    category?: string
  ): Promise<boolean> {
    const inactive = await this.inactiveCategoryNames()
    const parent = categoryParent?.trim() || category?.trim() || 'Общее'
    const leaf = category?.trim() || 'Общее'
    return !inactive.has(parent) && !inactive.has(leaf)
  }

  /** Включить/выключить категорию (локально; облако — отдельным запросом). */
  async setCategoryActive(name: string, active: boolean): Promise<void> {
    const row = await this.db.categories.where('name').equals(name).first()
    if (row?.id != null) {
      await this.db.categories.update(row.id, {
        isActive: active,
        updatedAt: Date.now()
      })
    }
  }

  async listTopCategories(): Promise<
    { name: string; count: number; hasImage?: boolean }[]
  > {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return []
    const products = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    const inactive = await this.inactiveCategoryNames()
    const counts: Record<string, number> = {}
    for (const p of products) {
      if (p.isDeleted) continue
      const parent = p.categoryParent?.trim() || p.category?.trim() || 'Общее'
      const leaf = p.category?.trim() || 'Общее'
      if (inactive.has(parent) || inactive.has(leaf)) continue
      counts[parent] = (counts[parent] || 0) + 1
    }
    const categories = await this.db.categories.toArray()
    const order = new Map<string, number>()
    const images = new Map<string, boolean>()
    categories.forEach((c) => images.set(c.name, c.hasImage === true))
    categories
      .filter((c) => c.parentName == null && c.isActive)
      .forEach((c, index) => order.set(c.name, c.sortOrder ?? index))
    return Object.entries(counts)
      .filter(([name]) => !inactive.has(name))
      .map(([name, count]) => ({ name, count, hasImage: images.get(name) }))
      .sort((a, b) => {
        const ao = order.get(a.name)
        const bo = order.get(b.name)
        if (ao != null && bo != null && ao !== bo) return ao - bo
        if (ao != null && bo == null) return -1
        if (ao == null && bo != null) return 1
        return b.count - a.count
      })
  }

  /**
   * Листья выбранного верхнего уровня с количеством товаров.
   * Товары самого верхнего уровня (без листа) в список не попадают.
   */
  async listChildCategories(
    parentName: string
  ): Promise<{ name: string; count: number; hasImage?: boolean }[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return []
    const products = await this.db.products
      .where('[catalogVersionId+categoryParent+category]')
      .between([versionId, parentName, ''], [versionId, parentName, '\uffff'])
      .toArray()
    const inactive = await this.inactiveCategoryNames()
    const counts: Record<string, number> = {}
    for (const p of products) {
      if (p.isDeleted) continue
      const leaf = p.category?.trim()
      if (!leaf || leaf === parentName) continue
      if (inactive.has(leaf)) continue
      counts[leaf] = (counts[leaf] || 0) + 1
    }
    const categories = await this.db.categories.toArray()
    const order = new Map<string, number>()
    const images = new Map<string, boolean>()
    categories.forEach((c) => images.set(c.name, c.hasImage === true))
    categories
      .filter((c) => c.parentName === parentName && c.isActive)
      .forEach((c, index) => order.set(c.name, c.sortOrder ?? index))
    return Object.entries(counts)
      .filter(([name]) => !inactive.has(name))
      .map(([name, count]) => ({ name, count, hasImage: images.get(name) }))
      .sort((a, b) => {
        const ao = order.get(a.name)
        const bo = order.get(b.name)
        if (ao != null && bo != null && ao !== bo) return ao - bo
        if (ao != null && bo == null) return -1
        if (ao == null && bo != null) return 1
        return b.count - a.count
      })
  }

  /**
   * Товары поддерева: весь верхний уровень или конкретный лист.
   */
  async listProductsByCategoryPath(
    parentName: string,
    leaf?: string
  ): Promise<Product[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return []
    const inactive = await this.inactiveCategoryNames()
    if (inactive.has(parentName) || (leaf != null && inactive.has(leaf))) {
      return []
    }
    const list = leaf
      ? await this.db.products
          .where('[catalogVersionId+categoryParent+category]')
          .equals([versionId, parentName, leaf])
          .toArray()
      : await this.db.products
          .where('[catalogVersionId+categoryParent+category]')
          .between([versionId, parentName, ''], [versionId, parentName, '\uffff'])
          .toArray()
    return list
      .filter((p) => !p.isDeleted && p.isActive !== false)
      .filter((p) => !inactive.has(p.category?.trim() ?? ''))
      .sort(
        (a, b) =>
          (a.categoryOrdinal ?? Number.MAX_SAFE_INTEGER) -
            (b.categoryOrdinal ?? Number.MAX_SAFE_INTEGER) ||
          a.name.localeCompare(b.name, 'ru')
      )
  }

  /** Количество живых товаров в поддереве верхнего уровня. */
  async countProductsByCategoryPath(parentName: string): Promise<number> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return 0
    const inactive = await this.inactiveCategoryNames()
    if (inactive.has(parentName)) return 0
    const list = await this.db.products
      .where('[catalogVersionId+categoryParent+category]')
      .between([versionId, parentName, ''], [versionId, parentName, '\uffff'])
      .toArray()
    return list.filter(
      (p) => !p.isDeleted && !inactive.has(p.category?.trim() ?? '')
    ).length
  }

  /** Отметить наличие/отсутствие загруженной картинки у категории. */
  async setCategoryHasImage(name: string, hasImage: boolean): Promise<void> {
    const row = await this.db.categories.where('name').equals(name).first()
    if (row?.id != null) {
      await this.db.categories.update(row.id, {
        hasImage,
        updatedAt: Date.now()
      })
    }
  }

  /**
   * Сохранить порядок категорий локально (после drag&drop).
   * Нужен, чтобы список перестроился сразу, не дожидаясь облачного синка.
   */
  async setCategoryOrderLocal(
    items: Array<{ name: string; sortOrder: number }>
  ): Promise<void> {
    await this.db.transaction('rw', this.db.categories, async () => {
      for (const item of items) {
        const row = await this.db.categories.where('name').equals(item.name).first()
        if (row?.id != null) {
          await this.db.categories.update(row.id, {
            sortOrder: item.sortOrder,
            updatedAt: Date.now()
          })
        }
      }
    })
  }

  /** Удалить категорию из локального дерева (после удаления в облаке). */
  async removeCategoryLocal(name: string): Promise<void> {
    await this.db.transaction('rw', this.db.categories, async () => {
      const rows = await this.db.categories.where('name').equals(name).toArray()
      for (const row of rows) {
        if (row.id != null) await this.db.categories.delete(row.id)
      }
    })
  }

  /**
   * Бэкфилл инварианта «верхний уровень хранит parent = category».
   *
   * Старые импорты оставляли `categoryParent` пустым: плитки категорий
   * рисовались, но выборка товаров по составному индексу [parent+leaf]
   * ничего не находила («в категории нет товаров»). Идемпотентно: трогаем
   * только пустые строки.
   */
  async normalizeCategoryParents(): Promise<number> {
    const rows = await this.db.products
      .filter((p) => !p.categoryParent && !!p.category && !p.isDeleted)
      .toArray()
    if (rows.length === 0) return 0
    await this.db.products.bulkPut(
      rows.map((p) => ({
        ...p,
        categoryParent: p.category as string,
        updatedAt: Date.now()
      }))
    )
    return rows.length
  }

  /** Полное дерево категорий (включая неактивные) для админки. */
  async listCategoryTree(): Promise<Category[]> {
    const rows = await this.db.categories.toArray()
    return rows.sort((a, b) => {
      const ap = a.parentName ?? ''
      const bp = b.parentName ?? ''
      if (ap !== bp) return ap.localeCompare(bp, 'ru')
      return (
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        a.name.localeCompare(b.name, 'ru')
      )
    })
  }

  /**
   * Импортировать справочник категорий (upsert по имени).
   * Дерево tenant-scoped и не входит в версию каталога.
   */
  async importCategories(
    rows: ParsedCategory[]
  ): Promise<{ added: number; updated: number }> {
    const now = Date.now()
    let added = 0
    let updated = 0
    await this.db.transaction('rw', this.db.categories, async () => {
      for (const row of rows) {
        const existing = await this.db.categories
          .where('name')
          .equals(row.name)
          .first()
        const record: Category = {
          name: row.name,
          parentName: row.parentName,
          department: row.department,
          isActive: row.isActive,
          sortOrder: row.sortOrder,
          imageUrl: row.imageUrl,
          // Загруженная картинка живёт отдельно; импорт её не сбрасывает.
          hasImage: existing?.hasImage ?? false,
          updatedAt: now
        }
        if (existing?.id != null) {
          await this.db.categories.update(existing.id, record)
          updated++
        } else {
          await this.db.categories.add(record)
          added++
        }
      }
    })
    return { added, updated }
  }

  /**
   * Подставить `categoryParent` товарам по справочнику категорий.
   * Неизвестные категории становятся верхним уровнем и дают предупреждение.
   */
  async resolveCategoryParents(
    rows: ParsedRow[]
  ): Promise<{ rows: ParsedRow[]; warnings: string[] }> {
    const categories = await this.db.categories.toArray()
    const byName = new Map(categories.map((c) => [c.name, c]))
    const unknown = new Set<string>()
    const resolved = rows.map((row) => {
      const leaf = row.category?.trim() || 'Общее'
      const category = byName.get(leaf)
      if (!category) {
        if (leaf !== 'Общее') unknown.add(leaf)
        return { ...row, category: leaf, categoryParent: leaf }
      }
      return {
        ...row,
        category: leaf,
        categoryParent: category.parentName ?? leaf
      }
    })
    const warnings = [...unknown].map(
      (name) =>
        `Категория «${name}» не найдена в дереве — показана как верхний уровень`
    )
    return { rows: resolved, warnings }
  }

  /**
   * Живой текстовый поиск по наименованию/штрихкоду в активной версии.
   * Нужен для товаров без штрихкода (находятся по названию). Пустой запрос —
   * возвращает все активные товары. Результат ограничен 50 позициями.
   */
  async searchProducts(query: string): Promise<Product[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return []
    const q = query.trim().toLowerCase()
    if (!q) return this.listActiveProducts()
    const list = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    const inactive = await this.inactiveCategoryNames()
    return list
      .filter(
        (p) =>
          !p.isDeleted &&
          p.isActive !== false &&
          !inactive.has(p.categoryParent?.trim() || p.category?.trim() || 'Общее') &&
          !inactive.has(p.category?.trim() || 'Общее') &&
          (p.name.toLowerCase().includes(q) || (!!p.barcode && p.barcode.includes(q)))
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      .slice(0, 50)
  }

  /**
   * Импортировать распарсенные строки как НОВУЮ версию каталога.
   * Создаёт запись версии, добавляет товары с её id и делает версию активной.
   * @returns созданная версия с проставленным id.
   */
  async importAsNewVersion(
    rows: ParsedRow[],
    versionName: string,
    note?: string
  ): Promise<CatalogVersion> {
    const trimmedName = versionName.trim()
    if (!trimmedName) {
      throw new Error('Имя версии не может быть пустым')
    }
    const existing = await this.db.catalogVersions
      .where('versionName')
      .equals(trimmedName)
      .first()
    if (existing) {
      throw new Error(`Версия с именем «${trimmedName}» уже существует`)
    }

    const resolved = await this.resolveCategoryParents(rows)
    const now = Date.now()
    return this.db.transaction('rw', this.db.catalogVersions, this.db.products, this.db.meta, async () => {
      const versionId = (await this.db.catalogVersions.add({
        versionName: trimmedName,
        note,
        source: 'local',
        createdAt: now
      })) as number

      // Нумерация плиток: порядок строки в выгрузке внутри категории.
      const ordinalCounters = new Map<string, number>()
      const products: Product[] = resolved.rows.map((row) => {
        const unit = row.unit ?? 'шт'
        const category = row.category ?? 'Общее'
        const categoryParent = row.categoryParent ?? category
        const key = `${categoryParent}\u0000${category}`
        const categoryOrdinal = (ordinalCounters.get(key) ?? 0) + 1
        ordinalCounters.set(key, categoryOrdinal)
        return {
          catalogVersionId: versionId,
          barcode: row.barcode ?? '',
          name: row.name,
          price: row.price,
          stock: row.stock ?? 0,
          unit,
          category,
          categoryParent,
          categoryOrdinal,
          isActive: true,
          sourceMeta: row.sourceMeta,
          subcategory: row.subcategory?.trim() || undefined,
          isWeight: row.isWeight ?? unit === 'кг',
          requiresMarking: row.requiresMarking,
          updatedAt: now
        }
      })
      await this.db.products.bulkAdd(products)
      await this.db.meta.put({ key: META_ACTIVE_VERSION, value: versionId })

      return { id: versionId, versionName: trimmedName, note, createdAt: now }
    })
  }

  /**
   * Инлайн-обновление цены товара (вызов из AdminPage onBlur).
   * Обновляет цену в самой строке активной версии (цены — это оперативное
   * редактирование директора; версия служит для отката импортов каталога).
   */
  async updatePrice(productId: number, price: number): Promise<{ queued: boolean }> {
    if (!Number.isFinite(price) || price < 0) {
      throw new Error('Цена должна быть неотрицательным числом')
    }
    const product = await this.db.products.get(productId)
    const updated = await this.db.products.update(productId, {
      price,
      updatedAt: Date.now()
    })
    if (updated === 0) {
      throw new Error(`Товар #${productId} не найден`)
    }
    // В облако — отложенно (ARCH-TASK-60.3): при обрыве связи цена не теряется
    // и не затирается дельтой каталога.
    if (product?.cloudId == null) {
      return { queued: false }
    }
    await enqueuePendingEdit(product.cloudId, { price })
    return { queued: true }
  }

  /**
   * Инлайн-правка минимального остатка товара (ARCH-TASK-64a).
   *
   * `value = null` — сбросить на общий порог магазина. Как и цена, правка
   * уходит в облако отложенно и не затирается дельтой каталога.
   */
  async updateMinStock(productId: number, value: number | null): Promise<{ queued: boolean }> {
    if (value != null && (!Number.isFinite(value) || value < 0)) {
      throw new Error('Минимальный остаток должен быть неотрицательным числом')
    }
    const product = await this.db.products.get(productId)
    const updated = await this.db.products.update(productId, {
      minStock: value ?? undefined,
      updatedAt: Date.now()
    })
    if (updated === 0) {
      throw new Error(`Товар #${productId} не найден`)
    }
    if (product?.cloudId == null) {
      return { queued: false }
    }
    await enqueuePendingEdit(product.cloudId, { minStock: value })
    return { queued: true }
  }

  /**
   * Инлайн-правка полей товара из админки: наименование, штрихкод, категория,
   * маркировка. Локально применяем сразу, в облако — через очередь правок,
   * чтобы дельта каталога не затёрла изменения при обрыве связи.
   *
   * Смена категории переносит товар в конец новой категории: номер плитки
   * пересчитывается, как при импорте.
   */
  async updateProductFields(
    productId: number,
    patch: {
      name?: string
      barcode?: string
      category?: string
      categoryParent?: string
      requiresMarking?: boolean
    }
  ): Promise<{ queued: boolean }> {
    const product = await this.db.products.get(productId)
    if (!product) throw new Error(`Товар #${productId} не найден`)

    const next: Partial<Product> = { updatedAt: Date.now() }
    const edit: {
      name?: string
      barcode?: string
      category?: string
      categoryParent?: string
      requiresMarking?: boolean
    } = {}

    if (patch.name !== undefined) {
      const name = patch.name.trim()
      if (!name) throw new Error('Наименование не может быть пустым')
      next.name = name
      edit.name = name
    }
    if (patch.barcode !== undefined) {
      const barcode = patch.barcode.trim()
      next.barcode = barcode
      edit.barcode = barcode
    }
    if (patch.category !== undefined) {
      const category = patch.category.trim() || 'Общее'
      next.category = category
      edit.category = category
    }
    if (patch.categoryParent !== undefined) {
      const categoryParent = patch.categoryParent.trim()
      next.categoryParent = categoryParent
      edit.categoryParent = categoryParent
    }
    if (patch.requiresMarking !== undefined) {
      next.requiresMarking = patch.requiresMarking
      edit.requiresMarking = patch.requiresMarking
    }

    if (patch.category !== undefined || patch.categoryParent !== undefined) {
      const category = next.category ?? product.category ?? 'Общее'
      const parent = next.categoryParent ?? product.categoryParent ?? category
      const rows = await this.db.products
        .where('catalogVersionId')
        .equals(product.catalogVersionId)
        .toArray()
      const maxOrdinal = rows
        .filter(
          (row) =>
            row.id !== productId &&
            !row.isDeleted &&
            (row.categoryParent?.trim() || row.category?.trim() || 'Общее') === parent &&
            (row.category?.trim() || 'Общее') === category
        )
        .reduce((max, row) => Math.max(max, row.categoryOrdinal ?? 0), 0)
      next.categoryOrdinal = maxOrdinal + 1
    }

    await this.db.products.update(productId, next)

    if (product.cloudId == null) return { queued: false }
    await enqueuePendingEdit(product.cloudId, edit)
    return { queued: true }
  }

  /**
   * Найти товар по штрихкоду в АКТИВНОЙ версии каталога.
   * Использует составной индекс [catalogVersionId+barcode] — быстрый точечный
   * поиск при сканировании на кассе.
   * @returns товар или null, если не найден.
   */
  async findByBarcode(barcode: string): Promise<Product | null> {
    const code = barcode.trim()
    if (!code) {
      return null
    }
    const versionId = await this.getActiveVersionId()
    if (versionId == null) {
      return null
    }
    const found = await this.db.products
      .where('[catalogVersionId+barcode]')
      .equals([versionId, code])
      .first()
    return found ?? null
  }

  /**
   * Найти товар по артикулу из весового штрихкода (префиксы 20-29).
   * Ищет в активной версии товар, чей barcode содержит 5-значный артикул
   * (напр. весовой PLU-код записан в поле barcode товара).
   */
  async findByItemCode(itemCode: string): Promise<Product | null> {
    const code = itemCode.trim()
    if (!code) {
      return null
    }
    const versionId = await this.getActiveVersionId()
    if (versionId == null) {
      return null
    }
    const products = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    const found = products.find(
      (p) => !p.isDeleted && (p.barcode === code || p.barcode.includes(code))
    )
    return found ?? null
  }

  /**
   * Создать товар «на лету» прямо на кассе — в активной версии каталога.
   * Помечается isCreatedOnPos для последующей ревизии директором.
   * @returns созданный товар с проставленным id.
   */
  async createOnPos(input: {
    name: string
    price: number
    barcode?: string
    /** Весовой товар: цена за кг, unit = 'кг' (ARCH-TASK-60.7). */
    isWeight?: boolean
    /** Требует кода маркировки при продаже. */
    requiresMarking?: boolean
    /** Имя кассира, создавшего товар (уходит в заявку для аудита). */
    createdByName?: string
  }): Promise<Product> {
    const name = input.name.trim()
    if (!name) {
      throw new Error('Укажите наименование товара')
    }
    if (!Number.isFinite(input.price) || input.price < 0) {
      throw new Error('Цена должна быть неотрицательным числом')
    }
    const barcode = input.barcode?.trim() ?? ''
    const versionId = await this.ensureActiveVersion()

    // Защита от дубликатов: в активной версии не должно быть живого товара
    // с таким же штрихкодом.
    if (barcode) {
      const existing = await this.db.products
        .where('[catalogVersionId+barcode]')
        .equals([versionId, barcode])
        .first()
      if (existing && !existing.isDeleted) {
        throw new Error(
          `Товар со штрихкодом "${barcode}" уже существует: "${existing.name}" (${existing.price} ₽)`
        )
      }
    }

    const now = Date.now()
    const existingRows = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    const categoryOrdinal = nextOrdinal(
      existingRows.filter((p) => (p.category ?? 'Общее') === 'Общее')
    )
    const product: Product = {
      catalogVersionId: versionId,
      barcode,
      name,
      price: input.price,
      stock: 0,
      unit: input.isWeight ? 'кг' : 'шт',
      category: 'Общее',
      categoryParent: 'Общее',
      categoryOrdinal,
      isActive: true,
      isWeight: Boolean(input.isWeight),
      requiresMarking: Boolean(input.requiresMarking),
      isCreatedOnPos: true,
      createdByName: input.createdByName,
      clientUuid: generateUUID(),
      cloudStatus: 'pending',
      isDeleted: false,
      updatedAt: now
    }
    const id = (await this.db.products.add(product)) as number
    return { ...product, id }
  }

  /**
   * Создать товар из админки: обычная позиция каталога, не заявка с кассы.
   * `cloudId` передаётся, когда товар уже создан в облаке.
   */
  async createProductAdmin(input: {
    name: string
    price: number
    barcode?: string
    category?: string
    categoryParent?: string
    requiresMarking?: boolean
    isWeight?: boolean
    cloudId?: number
  }): Promise<Product> {
    const name = input.name.trim()
    if (!name) throw new Error('Укажите наименование товара')
    if (!Number.isFinite(input.price) || input.price < 0) {
      throw new Error('Цена должна быть неотрицательным числом')
    }
    const category = input.category?.trim() || 'Общее'
    const categoryParent = input.categoryParent?.trim() || category
    const versionId = await this.ensureActiveVersion()
    const rows = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    const categoryOrdinal = nextOrdinal(
      rows.filter(
        (p) =>
          !p.isDeleted &&
          (p.categoryParent?.trim() || p.category?.trim() || 'Общее') ===
            categoryParent &&
          (p.category?.trim() || 'Общее') === category
      )
    )
    const product: Product = {
      catalogVersionId: versionId,
      barcode: input.barcode?.trim() ?? '',
      name,
      price: input.price,
      stock: 0,
      unit: input.isWeight ? 'кг' : 'шт',
      category,
      categoryParent,
      categoryOrdinal,
      isActive: true,
      isWeight: Boolean(input.isWeight),
      requiresMarking: Boolean(input.requiresMarking),
      isCreatedOnPos: false,
      cloudId: input.cloudId,
      isDeleted: false,
      updatedAt: Date.now()
    }
    const id = (await this.db.products.add(product)) as number
    return { ...product, id }
  }

  /** Товары активной версии, созданные кассирами «на лету» (для модерации). */
  async listCreatedOnPosProducts(): Promise<Product[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) {
      return []
    }
    const products = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    return products
      .filter((p) => p.isCreatedOnPos && !p.isDeleted)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }

  /** Обновить поля товара (модерация директором). */
  async updateProduct(id: number, patch: Partial<Product>): Promise<void> {
    const before = await this.db.products.get(id)
    const allowed: Partial<Product> = {}
    let shouldResend = false
    if (patch.name != null) {
      allowed.name = patch.name
      shouldResend = true
    }
    if (patch.price != null) {
      allowed.price = patch.price
      shouldResend = true
    }
    if (patch.barcode != null) {
      allowed.barcode = patch.barcode
      shouldResend = true
    }
    if (patch.stock != null) allowed.stock = patch.stock
    if (patch.requiresMarking != null) {
      allowed.requiresMarking = patch.requiresMarking
      shouldResend = true
    }
    if (patch.category != null) {
      allowed.category = patch.category
      const category = await this.db.categories
        .where('name')
        .equals(patch.category)
        .first()
      allowed.categoryParent = category?.parentName ?? patch.category
      shouldResend = true
    }
    if (patch.isActive != null) {
      allowed.isActive = patch.isActive
      shouldResend = true
    }
    if (patch.isWeight != null) {
      allowed.isWeight = patch.isWeight
      shouldResend = true
    }
    allowed.updatedAt = Date.now()
    // Правка полей заявки после отправки: сбрасываем отметку, чтобы синк
    // отправил актуальные данные повторно (сервер обновляет только pending).
    if (shouldResend) allowed.cloudSyncedAt = 0
    await this.db.products.update(id, allowed)
    // Смена категории: перенумеровать старую и новую группы.
    if (patch.category != null && before) {
      await this.renumberCategory(before.categoryParent, before.category)
      await this.renumberCategory(allowed.categoryParent, allowed.category)
    }
  }

  /**
   * Перенумеровать живые товары категории 1..N.
   * Вызывается после удаления и смены категории.
   */
  async renumberCategory(
    categoryParent?: string,
    category?: string
  ): Promise<void> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return
    const parent = categoryParent?.trim() || category?.trim() || 'Общее'
    const leaf = category?.trim() || 'Общее'
    const rows = await this.db.products
      .where('[catalogVersionId+categoryParent+category]')
      .equals([versionId, parent, leaf])
      .toArray()
    const ordinals = assignOrdinals(rows)
    await this.db.transaction('rw', this.db.products, async () => {
      for (const row of rows) {
        if (row.id == null) continue
        const next = ordinals.get(row.id)
        if (next != null && next !== row.categoryOrdinal) {
          await this.db.products.update(row.id, { categoryOrdinal: next })
        }
      }
    })
  }

  /**
   * Деактивировать/активировать товар.
   * Номер сохраняется: у неактивного товара он просто скрыт.
   */
  /**
   * Частота продаж товаров, которые кассир выбирает вручную, за окно
   * (по умолчанию 7 дней). Ручные — товары без штрихкода и весовые:
   * сканер их не находит, кассир ищет их плиткой или поиском.
   *
   * Возвраты вычитаются; корзина и коррекции не учитываются. Ключ товара —
   * `productKey` (штрихкод либо нормализованное имя).
   */
  async getFrequencyStats(
    windowMs: number = FREQUENCY_WINDOW_MS
  ): Promise<{ products: Map<string, number>; categories: Map<string, number> }> {
    const now = Date.now()
    const since = now - windowMs
    const receipts = await this.db.receipts.where('createdAt').above(since).toArray()
    const docs: SalesDocRef[] = receipts
      .filter((r) => !r.trash && r.docType !== 'correction')
      .map((r) => ({
        createdAt: r.createdAt,
        isReturn: (r.settlementType ?? 'income') === 'income_return',
        items: r.items.map((item) => ({
          barcode: item.barcode,
          name: item.name,
          qty: item.qty
        }))
      }))

    const versionId = await this.getActiveVersionId()
    const products =
      versionId == null
        ? []
        : await this.db.products.where('catalogVersionId').equals(versionId).toArray()

    const byBarcode = new Map<string, string>()
    const byName = new Map<string, string>()
    const keyCategory = new Map<string, string>()
    for (const product of products) {
      if (product.isDeleted) continue
      const manual = product.isWeight === true || product.unit === 'кг' || !product.barcode?.trim()
      if (!manual) continue
      const key = productKey(product.barcode, product.name)
      const barcode = product.barcode?.trim()
      if (barcode) byBarcode.set(barcode, key)
      byName.set(normalizeName(product.name), key)
      keyCategory.set(key, (product.categoryParent || product.category || 'Общее').trim())
    }

    // Строка чека ручная, только если её удалось сопоставить ручному товару.
    const resolveKey = (line: { barcode: string; name?: string }): string | null =>
      byBarcode.get(line.barcode?.trim() ?? '') ??
      byName.get(normalizeName(line.name)) ??
      null

    const productStats = aggregateFrequency(docs, resolveKey, now, windowMs)
    return { products: productStats, categories: rollupByCategory(productStats, keyCategory) }
  }

  async setProductActive(id: number, active: boolean): Promise<void> {
    await this.db.products.update(id, {
      isActive: active,
      updatedAt: Date.now()
    })
  }

  /** Мягкое удаление товара (скрыть из каталога, сохранить в БД). */
  async deleteProduct(id: number): Promise<void> {
    const product = await this.db.products.get(id)
    await this.db.products.update(id, { isDeleted: true, updatedAt: Date.now() })
    // Удаление закрывает «дыру» в нумерации категории.
    await this.renumberCategory(product?.categoryParent, product?.category)
    // Отправленную заявку отменяем в облаке (best-effort): если товар ещё
    // pending — сервер помечает rejected, если approved — 409, игнорируем.
    if (product?.isCreatedOnPos && product.cloudSyncedAt && product.clientUuid) {
      void syncService.cancelPending(product.clientUuid).catch(() => undefined)
    }
  }

  /** Товары корзины (мягко удалённые) активной версии. */
  async listDeletedProducts(): Promise<Product[]> {
    const versionId = await this.getActiveVersionId()
    if (versionId == null) return []
    const rows = await this.db.products
      .where('catalogVersionId')
      .equals(versionId)
      .toArray()
    return rows
      .filter((p) => p.isDeleted === true)
      .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
  }

  /** Восстановить товар из корзины (локально; облако — отдельным запросом). */
  async restoreProduct(id: number): Promise<void> {
    await this.db.products.update(id, { isDeleted: false, updatedAt: Date.now() })
    const product = await this.db.products.get(id)
    await this.renumberCategory(product?.categoryParent, product?.category)
  }

  /** Окончательно удалить позиции корзины локально. */
  async purgeDeleted(ids?: number[]): Promise<number> {
    const rows = await this.db.products.filter((p) => p.isDeleted === true).toArray()
    const targets = ids
      ? rows.filter((p) => p.id != null && ids.includes(p.id))
      : rows
    for (const row of targets) {
      if (row.id != null) await this.db.products.delete(row.id)
    }
    return targets.length
  }

  /**
   * Бэкфилл источника версии для записей, созданных до ARCH-TASK-60:
   * облачные версии помечаются, чтобы у них появился журнал изменений.
   */
  async ensureVersionSources(): Promise<void> {
    const versions = await this.db.catalogVersions.toArray()
    for (const version of versions) {
      if (version.id == null || version.source) continue
      if ((version.note ?? '').includes('из облака')) {
        await this.db.catalogVersions.update(version.id, { source: 'cloud' })
      }
    }
  }

  /**
   * Разрешено ли кассирам заводить неизвестные товары на кассе «на лету».
   * По умолчанию true (если флаг ещё не задавался директором).
   */
  async isOnTheFlyAllowed(): Promise<boolean> {
    const entry = await this.db.meta.get(META_ALLOW_ON_THE_FLY)
    return typeof entry?.value === 'boolean' ? entry.value : true
  }

  /** Установить право кассиров на добавление товаров «на лету». */
  async setOnTheFlyAllowed(allowed: boolean): Promise<void> {
    await this.db.meta.put({ key: META_ALLOW_ON_THE_FLY, value: allowed })
  }

  /** Валюта магазина ('RUB' по умолчанию). */
  async getStoreCurrency(): Promise<CurrencyCode> {
    const entry = await this.db.meta.get(META_STORE_CURRENCY)
    return entry?.value === 'UZS' ? 'UZS' : 'RUB'
  }

  /** Переключить валюту магазина (RUB/UZS). */
  async setStoreCurrency(currency: CurrencyCode): Promise<void> {
    await this.db.meta.put({ key: META_STORE_CURRENCY, value: currency })
  }

  /**
   * Порог «товар заканчивается» (ARCH-TASK-62c): в единицах товара.
   * По умолчанию 3 (и для штучных, и для весовых).
   */
  async getLowStockThreshold(): Promise<number> {
    const entry = await this.db.meta.get(META_LOW_STOCK_THRESHOLD)
    const value = Number(entry?.value)
    return Number.isFinite(value) && value >= 0 ? value : 3
  }

  /** Задать порог «товар заканчивается». */
  async setLowStockThreshold(threshold: number): Promise<void> {
    if (!Number.isFinite(threshold) || threshold < 0) {
      throw new Error('Порог должен быть неотрицательным числом')
    }
    await this.db.meta.put({ key: META_LOW_STOCK_THRESHOLD, value: threshold })
  }

  /**
   * Показывать ли на кассе блок «Часто покупают» (быстрые товары).
   * По умолчанию выключено: блок не отнимает место у плиток категорий.
   */
  async isFrequentProductsEnabled(): Promise<boolean> {
    const entry = await this.db.meta.get(META_FREQUENT_PRODUCTS_ENABLED)
    return entry?.value === true
  }

  /** Включить/выключить блок «Часто покупают» на кассе. */
  async setFrequentProductsEnabled(enabled: boolean): Promise<void> {
    await this.db.meta.put({ key: META_FREQUENT_PRODUCTS_ENABLED, value: enabled })
  }
}
